import {GraphQLError} from 'graphql';
import {CombinedGraphQLErrors} from '@apollo/client';
import {ErrorLike} from '@apollo/client';

export type NormalizedError = {
	code: string;
	message: string;
	networkError?: boolean;
};

type ParsedError = {
	code: string;
	message: string;
};

const NETWORK_MESSAGE =
	'Произошла ошибка подключения к серверу. Возможно не доступен сервер или у вас проблемы с интернетом.';

const DEFAULT_ERROR: NormalizedError = {
	message: 'Произошла неизвестная ошибка',
	code: 'UNKNOWN_ERROR',
};

function fromGraphQLError(error: GraphQLError): ParsedError {
	return {code: String(error?.extensions?.code || ''), message: error.message};
}

/**
 * Разбирает ошибку Apollo.
 *
 * В Apollo Client 4 обёртки ApolloError больше нет: ошибки GraphQL приходят
 * в CombinedGraphQLErrors, а сетевые — самой ошибкой, без поля networkError.
 */
export function getError(
	apolloError?: ErrorLike | readonly GraphQLError[] | null | undefined,
	withNetworkMessage: boolean = true,
): ParsedError | null {
	if (!apolloError) return null;

	if (Array.isArray(apolloError)) {
		const first = apolloError[0];
		return first ? fromGraphQLError(first) : {...DEFAULT_ERROR};
	}

	const error = apolloError as ErrorLike;

	if (CombinedGraphQLErrors.is(error)) {
		const first = error.errors[0];
		if (first) return fromGraphQLError(first as GraphQLError);
	}

	if (isNetworkError(error)) {
		return withNetworkMessage ? {code: error.name, message: NETWORK_MESSAGE} : {...DEFAULT_ERROR};
	}

	return {...DEFAULT_ERROR};
}

/** сетевой сбой — всё, что не пришло от самого GraphQL-сервера */
function isNetworkError(error: ErrorLike): boolean {
	return !CombinedGraphQLErrors.is(error);
}

export function getQueryError(errors?: readonly GraphQLError[]): NormalizedError {
	if (!errors || errors.length) return DEFAULT_ERROR;
	return {
		message: errors[0]?.message || DEFAULT_ERROR.message,
		code: String(errors[0]?.extensions?.code || ''),
	};
}

export function getApolloError(error?: ErrorLike | null): NormalizedError {
	const err: NormalizedError | null = getError(error, true);
	if (!err || !error) return DEFAULT_ERROR;
	if (isNetworkError(error)) err.networkError = true;
	return err;
}
